import React from "react";
import { useTranslation } from "react-i18next";

import { LongShortButton } from "../LongShortButton";

import { ConnectButtonStyled } from "./styled";

// ---------------------------------------------------------------------------
// Pure presentational button — NO observer, NO store reads.
// All props are primitives / stable refs, so React.memo prevents re-renders
// when the parent (observer) re-renders but values haven't actually changed.
// ---------------------------------------------------------------------------
export interface TradeboxOrderButtonProps {
  isConnected: boolean;
  isRestricted: boolean;
  exceedsMax: boolean;
  hasFundingBalance: boolean;
  tradingIsEmpty: boolean;
  isEmpty: boolean;
  direction: "long" | "short";
  directionLabel: string;
  isButtonDisabled: boolean;
  isCreatingOrder: boolean;
  sizeFormatted: string;
  baseSymbol: string | undefined;
  handleCreateOrder: () => void;
  handleDepositClick: () => void;
  handleTransferClick: () => void;
  handleConnectWallet: () => void;
}

export const TradeboxOrderButton: React.FC<TradeboxOrderButtonProps> = React.memo(
  ({
    isConnected,
    isRestricted,
    exceedsMax,
    hasFundingBalance,
    tradingIsEmpty,
    isEmpty,
    direction,
    directionLabel,
    isButtonDisabled,
    isCreatingOrder,
    sizeFormatted,
    baseSymbol,
    handleCreateOrder,
    handleDepositClick,
    handleTransferClick,
    handleConnectWallet,
  }) => {
    const { t } = useTranslation();

    if (isRestricted) {
      if (!isConnected) {
        return (
          <ConnectButtonStyled data-testid="tradebox:connect-wallet" size="3" disabled onClick={handleConnectWallet}>
            {t("geoFence.connectBlocked")}
          </ConnectButtonStyled>
        );
      }

      return (
        <LongShortButton data-testid="tradebox:submit" size="3" variant={direction} disabled>
          {t("geoFence.actionBlocked")}
        </LongShortButton>
      );
    }

    if (!isConnected) {
      return (
        <ConnectButtonStyled data-testid="tradebox:connect-wallet" size="3" onClick={handleConnectWallet}>
          {t("tradebox.connectWallet")}
        </ConnectButtonStyled>
      );
    }

    if (hasFundingBalance && (tradingIsEmpty || exceedsMax)) {
      return (
        <LongShortButton data-testid="tradebox:submit" size="3" variant={direction} onClick={handleTransferClick}>
          {t("tradebox.transferAndDirection", { direction: directionLabel })}
        </LongShortButton>
      );
    }

    if (exceedsMax) {
      return (
        <LongShortButton data-testid="tradebox:submit" size="3" variant={direction} onClick={handleDepositClick}>
          {t("tradebox.depositAndDirection", { direction: directionLabel })}
        </LongShortButton>
      );
    }

    if (isEmpty) {
      return (
        <LongShortButton data-testid="tradebox:submit" size="3" variant={direction} disabled>
          {t("tradebox.enterAmount")}
        </LongShortButton>
      );
    }

    return (
      <LongShortButton
        data-testid="tradebox:submit"
        disabled={isButtonDisabled || isCreatingOrder}
        loading={isCreatingOrder}
        size="3"
        variant={direction}
        onClick={handleCreateOrder}
      >
        {t("tradebox.directionWithSize", {
          direction: directionLabel,
          size: sizeFormatted,
          symbol: baseSymbol,
        })}
      </LongShortButton>
    );
  },
);
